import React from 'react';
import { Input } from 'antd';
import { CaretDownOutlined, SearchOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useAuth } from '../../../../context/AuthContext';
import { AllowedRoles } from '../../../../routes/routeConstants/allowedRoles';
import type { WorkOrderFilterRule } from '../../../../types/InfrastructureManagerTypes';
import WorkOrderFilter from '../WorkOrderFilter';
import styles from './WorkOrdersTopBar.module.scss';

interface WorkOrdersTopBarProps {
  isSettingsView: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filters: WorkOrderFilterRule[];
  onFiltersChange: (filters: WorkOrderFilterRule[]) => void;
  onSettingsToggle: () => void;
  onSettingsPreload: () => void;
  onCreate: () => void;
}

const SEARCH_SUFFIX = <SearchOutlined aria-hidden />;

const handleHelpClick = () => {
  // Stub until the Help story lands.
};

const WorkOrdersTopBar = ({
  isSettingsView,
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange,
  onSettingsToggle,
  onSettingsPreload,
  onCreate,
}: WorkOrdersTopBarProps) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value);
  // Viewers are read-only, so they can't create work orders.
  const { canAccess } = useAuth();
  const canCreate = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });

  return (
    <header className={styles.workOrdersTopBar}>
      {isSettingsView ? (
        <h1 className={styles.topBarHeading}>Configuration Settings</h1>
      ) : (
        <div className={styles.topBarControls}>
          <WorkOrderFilter filters={filters} onFiltersChange={onFiltersChange}>
            <button
              type="button"
              className={clsx(styles.topBarFilter, filters.length && styles.topBarFilterActive)}
              aria-pressed={filters.length > 0}
            >
              Filter
              <CaretDownOutlined aria-hidden />
            </button>
          </WorkOrderFilter>
          <Input
            className={styles.topBarSearch}
            placeholder="SEARCH"
            aria-label="Search work orders"
            value={searchTerm}
            onChange={handleSearchChange}
            suffix={SEARCH_SUFFIX}
            allowClear
          />
        </div>
      )}

      <div className={styles.topBarActions}>
        <button
          type="button"
          className={`${styles.topBarIconButton} ${isSettingsView ? styles.topBarIconButtonActive : ''}`}
          aria-label={isSettingsView ? 'Close configuration settings' : 'Open configuration settings'}
          aria-pressed={isSettingsView}
          onClick={onSettingsToggle}
          onPointerEnter={onSettingsPreload}
          onFocus={onSettingsPreload}
        >
          <span className={`${styles.topBarIcon} ${styles.topBarIconSettings}`} aria-hidden />
        </button>
        <button type="button" className={styles.topBarIconButton} aria-label="Help" onClick={handleHelpClick}>
          <span className={`${styles.topBarIcon} ${styles.topBarIconHelp}`} aria-hidden />
        </button>
        {isSettingsView || !canCreate ? null : (
          <button type="button" className={styles.topBarIconButton} aria-label="Create work order" onClick={onCreate}>
            <span className={`${styles.topBarIcon} ${styles.topBarIconWorkOrder}`} aria-hidden />
          </button>
        )}
      </div>
    </header>
  );
};

export default WorkOrdersTopBar;
